/**
 * CR055/CR071/CR308/CR308-B: Sources table with logo, scan_type, web recipe column
 */

import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  MoreHorizontal,
  Pencil,
  Pause,
  Play,
  Archive,
  Trash2,
  ExternalLink,
  TestTube,
  Camera,
  ArrowUpDown,
  XCircle,
} from "lucide-react";
import { getSourceTypeLabel, normalizeSourceType, getSourceTypeUsage } from "@/lib/sourceTypes";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MerchantLogoCell } from "./MerchantLogoCell";
import { useWebRecipeNames } from "./useWebRecipeNames";
import type { MerchantSource, SourceStatus, ScanType } from "@/hooks/useMerchantSources";
import type { Merchant } from "@/hooks/useMerchants";
import { formatDistanceToNow } from "date-fns";

interface SourcesTableProps {
  sources: MerchantSource[];
  merchants?: Merchant[];
  onEdit: (source: MerchantSource) => void;
  onToggleStatus: (id: string, currentStatus: SourceStatus) => void;
  onDeprecate: (id: string) => void;
  onDelete: (id: string) => void;
  onTestRead: (source: MerchantSource) => void;
  onTestImageCapture?: (source: MerchantSource) => void;
  onUpdatePriority: (id: string, priority: number) => void;
  onUpdateLogo?: (merchantKey: string, data: { logo_url?: string | null; logo_source?: string }) => Promise<boolean>;
  onUploadLogo?: (merchantKey: string, file: File) => Promise<boolean>;
  onClearRecipeOverride?: (id: string) => void;
}

// CR308: Scan type filter options
type ScanTypeFilter = "all" | "WEB" | "FLYER";

export function SourcesTable({
  sources,
  merchants = [],
  onEdit,
  onToggleStatus,
  onDeprecate,
  onDelete,
  onTestRead,
  onTestImageCapture,
  onUpdatePriority,
  onUpdateLogo,
  onUploadLogo,
  onClearRecipeOverride,
}: SourcesTableProps) {
  const [search, setSearch] = useState("");
  const [scanTypeFilter, setScanTypeFilter] = useState<ScanTypeFilter>("all");
  const [hasOverrideOnly, setHasOverrideOnly] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<MerchantSource | null>(null);
  const [editingPriority, setEditingPriority] = useState<string | null>(null);
  const [tempPriority, setTempPriority] = useState<number>(100);

  // CR308-B: Lookup recipe names for display
  const recipeNames = useWebRecipeNames();

  const filteredSources = sources.filter((source) => {
    const searchLower = search.toLowerCase();
    const matchesSearch = 
      source.merchant_id.toLowerCase().includes(searchLower) ||
      (source.merchant_name?.toLowerCase().includes(searchLower)) ||
      source.source_url.toLowerCase().includes(searchLower) ||
      (source.source_label ?? "").toLowerCase().includes(searchLower);
    
    // CR308-B1: Treat missing scan_type as WEB (matches EditSourceModal default)
    const scanType = source.scan_type || "WEB";
    const matchesScanType = scanTypeFilter === "all" || scanType === scanTypeFilter;
    
    // CR308-B: Apply override filter (only meaningful for WEB sources)
    const matchesOverride = !hasOverrideOnly || (scanType === "WEB" && !!source.web_recipe_template_id);
    
    return matchesSearch && matchesScanType && matchesOverride;
  });

  // CR308-B: Count WEB sources with overrides for filter label
  const overrideCount = sources.filter(s => (s.scan_type || "WEB") === "WEB" && !!s.web_recipe_template_id).length;

  const getStatusBadge = (status: SourceStatus) => {
    switch (status) {
      case "active":
        return <Badge variant="default">Active</Badge>;
      case "paused":
        return <Badge variant="secondary">Paused</Badge>;
      case "deprecated":
        return <Badge variant="outline" className="text-muted-foreground">Deprecated</Badge>;
    }
  };

  const handlePriorityKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === "Enter") {
      onUpdatePriority(id, tempPriority);
      setEditingPriority(null);
    } else if (e.key === "Escape") {
      setEditingPriority(null);
    }
  };

  const truncateUrl = (url: string, maxLength = 50) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <Input
          placeholder="Search by merchant, URL, or label..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        
        {/* CR308: Scan type filter pills */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Scan:</span>
          <div className="flex gap-1">
            {(["all", "WEB", "FLYER"] as ScanTypeFilter[]).map((filter) => (
              <Button
                key={filter}
                variant={scanTypeFilter === filter ? "default" : "outline"}
                size="sm"
                onClick={() => setScanTypeFilter(filter)}
                className="h-7 px-2 text-xs"
              >
                {filter === "all" ? "All" : filter}
              </Button>
            ))}
          </div>
        </div>

        {/* CR308-B: Has Override filter */}
        {overrideCount > 0 && (
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <Checkbox
              checked={hasOverrideOnly}
              onCheckedChange={(v) => setHasOverrideOnly(!!v)}
            />
            <span className="text-muted-foreground">
              Has Recipe Override
              <Badge variant="secondary" className="ml-1 text-[10px] px-1 py-0">
                {overrideCount}
              </Badge>
            </span>
          </label>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Merchant</TableHead>
              <TableHead className="w-[80px]">
                <div className="flex items-center gap-1">
                  Priority
                  <ArrowUpDown className="h-3 w-3" />
                </div>
              </TableHead>
              <TableHead className="w-[90px]">Tier</TableHead>
              <TableHead className="w-[75px]">Scan</TableHead>
              <TableHead>Source URL</TableHead>
              <TableHead className="w-[140px]">Type</TableHead>
              <TableHead className="w-[130px]">Web Recipe</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="w-[120px]">Updated</TableHead>
              <TableHead className="w-[60px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSources.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                  {search || scanTypeFilter !== "all" || hasOverrideOnly
                    ? "No sources match your filters"
                    : "No sources configured"}
                </TableCell>
              </TableRow>
            ) : (
              filteredSources.map((source) => {
                const merchant = merchants.find((m) => m.merchant_key === source.merchant_id);
                const recipeName = source.web_recipe_template_id
                  ? recipeNames[source.web_recipe_template_id]
                  : null;

                return (
                <TableRow key={source.id} className={source.status === "deprecated" ? "opacity-50" : ""}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <MerchantLogoCell
                        merchantKey={source.merchant_id}
                        merchantName={source.merchant_name || source.merchant_id}
                        merchant={merchant}
                        onUpdateLogo={onUpdateLogo}
                        onUploadLogo={onUploadLogo}
                        size="sm"
                        editable={!!onUpdateLogo}
                      />
                      <div>
                        <div className="font-medium">{source.merchant_name || source.merchant_id}</div>
                        <div className="text-xs text-muted-foreground">{source.merchant_id}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {editingPriority === source.id ? (
                      <Input
                        type="number"
                        value={tempPriority}
                        onChange={(e) => setTempPriority(parseInt(e.target.value) || 1)}
                        onKeyDown={(e) => handlePriorityKeyDown(e, source.id)}
                        onBlur={() => {
                          onUpdatePriority(source.id, tempPriority);
                          setEditingPriority(null);
                        }}
                        className="w-16 h-7 text-sm"
                        autoFocus
                      />
                    ) : (
                      <button
                        className="text-sm hover:underline cursor-pointer"
                        onClick={() => {
                          setEditingPriority(source.id);
                          setTempPriority(source.priority);
                        }}
                      >
                        {source.priority}
                      </button>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* CR056: Source tier badge */}
                    {source.priority <= 9 ? (
                      <Badge variant="default" className="bg-primary text-primary-foreground">
                        PRIMARY
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        FALLBACK
                      </Badge>
                    )}
                  </TableCell>
                  {/* CR308: Scan type column (default missing to WEB) */}
                  <TableCell>
                    {(() => {
                      const scanType = source.scan_type || "WEB";
                      return (
                        <Badge 
                          variant={scanType === "WEB" ? "secondary" : "outline"}
                          className={scanType === "FLYER" ? "border-primary text-primary" : ""}
                        >
                          {scanType}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm truncate max-w-[300px]" title={source.source_url}>
                        {truncateUrl(source.source_url)}
                      </span>
                      <a
                        href={source.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                    {source.source_label && source.source_label !== source.source_type && (
                      <div className="text-xs text-muted-foreground">{source.source_label}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm font-medium cursor-help">
                            {getSourceTypeLabel(source.source_type)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[250px]">
                          <p className="text-xs font-mono mb-1">
                            Stored: {normalizeSourceType(source.source_type)}
                          </p>
                          <p className="text-xs">{getSourceTypeUsage(source.source_type)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  {/* CR308-B: Web Recipe column — only meaningful for WEB sources */}
                  <TableCell>
                    {(source.scan_type || "WEB") !== "WEB" ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : source.web_recipe_template_id ? (
                      <div className="flex items-center gap-1.5">
                        <Badge variant="default" className="text-[10px] px-1.5 py-0">
                          Override
                        </Badge>
                        <span className="text-xs truncate max-w-[80px]" title={recipeName || source.web_recipe_template_id}>
                          {recipeName || "…"}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Default</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(source.status)}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(source.updated_at), { addSuffix: true })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onTestRead(source)}>
                          <TestTube className="h-4 w-4 mr-2" />
                          Test Read
                        </DropdownMenuItem>
                        {/* CR454: Test Image Capture — FLYER sources with capture enabled + adapter set */}
                        {(() => {
                          const s = source as any;
                          const isFlyerCapture =
                            source.scan_type === "FLYER" &&
                            s.flyer_image_capture_enabled === true &&
                            !!s.flyer_image_capture_adapter_key;
                          const tooltipMsg = source.scan_type !== "FLYER"
                            ? "Only available for FLYER scan sources"
                            : !s.flyer_image_capture_enabled || !s.flyer_image_capture_adapter_key
                            ? "Enable image capture + select an adapter to test"
                            : null;

                          return (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <DropdownMenuItem
                                      disabled={!isFlyerCapture}
                                      onClick={() => isFlyerCapture && onTestImageCapture?.(source)}
                                    >
                                      <Camera className="h-4 w-4 mr-2" />
                                      Test Image Capture
                                    </DropdownMenuItem>
                                  </span>
                                </TooltipTrigger>
                                {tooltipMsg && (
                                  <TooltipContent side="left">
                                    <p className="text-xs max-w-[200px]">{tooltipMsg}</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })()}
                        <DropdownMenuItem onClick={() => onEdit(source)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {/* CR308-B: Clear recipe override action */}
                        {source.web_recipe_template_id && onClearRecipeOverride && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => onClearRecipeOverride(source.id)}>
                              <XCircle className="h-4 w-4 mr-2" />
                              Clear Recipe Override
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onToggleStatus(source.id, source.status)}>
                          {source.status === "active" ? (
                            <>
                              <Pause className="h-4 w-4 mr-2" />
                              Pause
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4 mr-2" />
                              Resume
                            </>
                          )}
                        </DropdownMenuItem>
                        {source.status !== "deprecated" && (
                          <DropdownMenuItem onClick={() => onDeprecate(source.id)}>
                            <Archive className="h-4 w-4 mr-2" />
                            Deprecate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => setDeleteConfirm(source)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );})
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Source?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the source for{" "}
              <strong>{deleteConfirm?.merchant_name || deleteConfirm?.merchant_id}</strong>:
              <br />
              <code className="text-xs">{deleteConfirm?.source_url}</code>
              <br /><br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteConfirm) {
                  onDelete(deleteConfirm.id);
                  setDeleteConfirm(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
